import { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Icon,
  Spinner,
  Collapse,
  Button,
} from '@chakra-ui/react';
import { CheckCircleIcon, WarningIcon, TimeIcon, ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { processErrorText } from '../../utils/statementHelpers';
import useIsPhone from '../../hooks/useIsPhone';
import { TINT } from './sourceFilesUI';

const STEP_CONFIG = [
  { number: 1, key: 'import' },
  { number: 2, key: 'match' },
  { number: 3, key: 'reconnect' },
  { number: 4, key: 'autoCreate' },
  { number: 5, key: 'categorize' },
];

function StepIcon({ status }) {
  const { t } = useTranslation('sourceFiles');
  if (status === 'completed') return <Icon as={CheckCircleIcon} color="redesign.accent.green" boxSize="14px" />;
  if (status === 'failed') return <Icon as={WarningIcon} color="redesign.accent.red" boxSize="14px" />;
  if (status === 'running') return <Spinner size="xs" color="redesign.accent.blue" label={t('pipeline.status.running')} />;
  return <Icon as={TimeIcon} color="redesign.text.faint" boxSize="14px" />;
}

function summaryLine(stepNumber, results, t) {
  if (!results) return null;
  const tx = results.transaction || {};
  const mv = results.movement || {};
  switch (stepNumber) {
    case 1: {
      const parts = [];
      if (tx.new) parts.push(`${tx.new} ${t('pipeline.metrics.new')}`);
      if (tx.existing) parts.push(`${tx.existing} ${t('pipeline.metrics.duplicates')}`);
      if (tx.filtered) parts.push(`${tx.filtered} ${t('pipeline.metrics.filtered')}`);
      return parts.join(' · ');
    }
    case 2:
      return tx.matched != null ? `${tx.matched} ${t('pipeline.metrics.matched')}` : null;
    case 3:
      return tx.matched != null ? `${tx.matched} ${t('pipeline.metrics.relinked')}` : null;
    case 4:
      return mv.created?.count != null ? `${mv.created.count} ${t('pipeline.metrics.created')}` : null;
    case 5:
      return mv.created?.count != null ? `${mv.created.count} ${t('pipeline.metrics.categorized')}` : null;
    default:
      return null;
  }
}

export default function SourceFileProcessingPipeline({
  process,
  stepsDetails,
  onRetry,
  onCancel,
}) {
  const { t } = useTranslation('sourceFiles');
  const isPhone = useIsPhone();
  const status = process?.status;
  const isProcessing = status === 'running' || status === 'pending';
  const isFailed = status === 'failed';
  const failedStep = stepsDetails?.find((s) => s.status === 'failed');
  const errorText = isFailed ? processErrorText(process, t) : null;

  const defaultExpanded = isProcessing || isFailed;
  const [expanded, setExpanded] = useState(defaultExpanded);

  useEffect(() => {
    setExpanded(isProcessing || isFailed);
  }, [isProcessing, isFailed]);

  const finalStep = stepsDetails?.find((s) => s.step_number === 5);
  const movementsCreated = finalStep?.results?.movement?.created?.count;
  const importStep = stepsDetails?.find((s) => s.step_number === 1);
  const txNew = importStep?.results?.transaction?.new;
  const txFiltered = importStep?.results?.transaction?.filtered;

  return (
    <Box
      borderWidth="1px"
      borderColor="redesign.border"
      borderRadius="lg"
      bg="redesign.bg.surface"
    >
      <Flex
        as="button"
        onClick={() => setExpanded((v) => !v)}
        align="center"
        justify="space-between"
        w="100%"
        px={4}
        py={3}
        // A tap target on the phone: 44px, which py alone leaves at 42.
        minH={isPhone ? '44px' : undefined}
        cursor="pointer"
        _hover={{ bg: 'redesign.bg.hover' }}
      >
        <HStack spacing={2}>
          <Icon
            as={expanded ? ChevronDownIcon : ChevronRightIcon}
            color="redesign.text.muted"
            boxSize="14px"
          />
          <Text fontSize="sm" fontWeight={600} color="redesign.heading">
            {t('pipeline.title')}
          </Text>
        </HStack>
        {!expanded && status === 'completed' && (
          <Text fontSize="xs" color="redesign.text.muted">
            {txNew != null ? `${txNew} ${t('pipeline.metrics.imported')}` : ''}
            {txFiltered ? ` · ${txFiltered} ${t('pipeline.metrics.filtered')}` : ''}
            {movementsCreated != null ? ` · ${movementsCreated} ${t('pipeline.metrics.created')}` : ''}
          </Text>
        )}
      </Flex>

      <Collapse in={expanded} animateOpacity>
        <Box px={4} pb={4} borderTop="1px solid" borderColor="redesign.border">
          {errorText && (
            <Box
              mt={3}
              p={3}
              borderRadius="md"
              {...TINT.red}
            >
              <Text fontSize="xs" fontWeight={600} color="redesign.accent.red" mb={1}>
                {t('pipeline.errorAtStep')} {failedStep?.step_number || '?'}
              </Text>
              <Text fontSize="xs" color="redesign.text">
                {errorText}
              </Text>
            </Box>
          )}

          <VStack spacing={3} align="stretch" mt={3}>
            {STEP_CONFIG.map((cfg) => {
              const data = stepsDetails?.find((s) => s.step_number === cfg.number);
              const stepStatus = data?.status || (isProcessing ? 'pending' : 'pending');
              const summary = summaryLine(cfg.number, data?.results, t);
              const isFailedStep = stepStatus === 'failed';

              return (
                <Flex
                  key={cfg.number}
                  align="center"
                  justify="space-between"
                  gap={3}
                  px={3}
                  py={2}
                  borderRadius="md"
                  {...(isFailedStep ? TINT.red : { bg: 'transparent' })}
                >
                  <HStack spacing={3} minW={0} flex="1">
                    <StepIcon status={stepStatus} />
                    <Text fontSize="xs" color="redesign.text" fontWeight={500}>
                      {t('pipeline.step')} {cfg.number}: {t(`pipeline.steps.${cfg.key}`)}
                    </Text>
                  </HStack>
                  <Text
                    fontSize="2xs"
                    color={isFailedStep ? 'redesign.accent.red' : 'redesign.text.muted'}
                    textAlign="right"
                    noOfLines={1}
                    // The status keeps its width ('Listo' was clipped to
                    // 'Li..' at 320) and the step name wraps instead; a long
                    // summary still ends in an ellipsis.
                    flexShrink={0}
                    maxW="55%"
                  >
                    {summary || (stepStatus === 'pending'
                      ? '—'
                      : t(`pipeline.status.${stepStatus}`, { defaultValue: stepStatus }))}
                  </Text>
                </Flex>
              );
            })}
          </VStack>

          {(isFailed || isProcessing) && (
            <HStack spacing={2} mt={4}>
              {isFailed && onRetry && (
                <Button
                  size="xs"
                  colorScheme="blue"
                  variant="outline"
                  onClick={() => onRetry(process.process_id)}
                >
                  {t('pipeline.retryFromFailed')}
                </Button>
              )}
              {isProcessing && onCancel && (
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => onCancel(process.process_id)}
                >
                  {t('pipeline.cancel')}
                </Button>
              )}
            </HStack>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
